/**
 * PromotionCode 信息卡：客户/已用/有效期/状态 + 默认折叠的 QR 码。
 * Detail.tsx（超管视角）和 DealerPortal（代理店自助）共用。
 */
import { Badge, Button, Card, Collapse, Group, Stack, Text } from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PromoCodeDetail } from '@mygpt/app/apis/services/dealer';

import { promoStatusBadge } from './promoStatus';
import { QRCodeCard } from './QRCodeCard';

interface Props {
  promo: PromoCodeDetail;
  onDeactivate: (promoId: string) => Promise<void> | void;
}

export const PromoCardItem = ({ promo: p, onDeactivate }: Props) => {
  const { t } = useTranslation();
  const [qrOpen, setQrOpen] = useState(false);

  const statusBadge = promoStatusBadge(p, t);

  return (
    <Card withBorder bg="gray.0">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Stack gap={4} flex={1}>
            <Group gap="xs">
              <Text fw={600} ff="monospace">
                {p.code}
              </Text>
              <Badge color={statusBadge.color} variant="light">
                {statusBadge.label}
              </Badge>
            </Group>
            <Text size="sm">
              {t('promotion_code.customer_label')}: {p.customer_email || t('promotion_code.customer_any')}
            </Text>
            <Text size="sm">
              {t('promotion_code.redeemed_label')}: {p.redeemed_count} / {p.max_redemptions}
            </Text>
            {p.redeemed_by && p.redeemed_by.length > 0 && (
              <Text size="sm">
                {t('promotion_code.redeemed_by_label')}: {p.redeemed_by.join(', ')}
              </Text>
            )}
            <Text size="sm">
              {t('promotion_code.expires_label')}:{' '}
              {p.expires_at ? new Date(p.expires_at).toLocaleString() : t('promotion_code.no_expiry')}
            </Text>
          </Stack>
          <Stack gap="xs" align="flex-end">
            <Button
              size="xs"
              variant="subtle"
              rightSection={qrOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
              onClick={() => setQrOpen((v) => !v)}
            >
              {qrOpen ? t('promotion_code.hide_qr') : t('promotion_code.show_qr')}
            </Button>
            <Button size="xs" color="red" variant="light" disabled={!p.is_active} onClick={() => onDeactivate(p.id)}>
              {t('dealer.deactivate')}
            </Button>
          </Stack>
        </Group>
        <Collapse in={qrOpen}>
          <Group justify="center">
            <QRCodeCard code={p.code} url={p.qr_code_url} size={200} />
          </Group>
        </Collapse>
      </Stack>
    </Card>
  );
};
